import { Component } from '@theme/component';

/* ---- Collection-owned search: index matching (pure helpers) ---- */

/** How long a collection's index stays in sessionStorage (it holds no prices or stock). */
const INDEX_TTL = 10 * 60 * 1000;
const INDEX_STORAGE_PREFIX = 'hl-collection-index:v1:';
/** Cards per request; the search template renders up to 50 for an id query (hl-collection-products). */
const CARD_BATCH = 32;

/** @param {string} text */
function fold(text) {
  return text.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Words of a product field. Hyphenated words count as their parts and joined ("T-Shirt" gives
 * t, shirt and tshirt), so hyphens never matter; apostrophes are dropped ("Women's" = womens).
 * @param {string} text
 */
function tokenize(text) {
  /** @type {string[]} */
  const tokens = [];
  for (const chunk of fold(text).replace(/['’]/g, '').split(/[^a-z0-9-]+/)) {
    const parts = chunk.split('-').filter(Boolean);
    tokens.push(...parts);
    if (parts.length > 1) tokens.push(parts.join(''));
  }
  return tokens;
}

/**
 * Words of a query: hyphens ignored ("t-shirt" = tshirt), "mt"/"mt." = mount, and a plural "s"
 * dropped on words over 3 letters ("hats" finds "Hat"; matching is by word start, so "hat" still
 * finds "Hats").
 * @param {string} query
 */
function queryWords(query) {
  return fold(query)
    .replace(/['’]/g, '')
    .replace(/-/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((word) => (word === 'mt' ? 'mount' : word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word));
}

/**
 * Theme settings > Search > "Search aliases": one "word: other word" per line.
 * @param {string} text
 * @returns {Map<string, string[]>}
 */
function parseAliases(text) {
  /** @type {Map<string, string[]>} */
  const aliases = new Map();
  for (const line of text.split(/\n/)) {
    const [from, to] = line.split(':');
    if (!from || !to) continue;
    const key = queryWords(from)[0];
    const value = queryWords(to)[0];
    if (!key || !value || key === value) continue;
    aliases.set(key, [...(aliases.get(key) ?? []), value]);
  }
  return aliases;
}

/**
 * At most one edit (insert, delete, change or swap of neighbours) between two short words.
 * @param {string} a
 * @param {string} b
 */
function withinOneEdit(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) {
    if (a.slice(i + 1) === b.slice(i + 1)) return true;
    return a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2);
  }
  return a.length > b.length ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

/**
 * @typedef {{ id: string, title: string[], all: string[] }} IndexEntry
 * @typedef {[number, string, string, string, string]} IndexRow - id, title, type, tags, colors
 */

/** @param {IndexRow[]} rows @returns {IndexEntry[]} */
function buildIndex(rows) {
  return rows.map(([id, title, type, tags, colors]) => {
    const titleTokens = tokenize(title);
    return {
      id: String(id),
      title: titleTokens,
      all: [...titleTokens, ...tokenize(type), ...tokenize(tags.replace(/\|/g, ' ')), ...tokenize(colors.replace(/\|/g, ' '))],
    };
  });
}

/**
 * Product ids matching every word of the query, in the collection's order, with goods whose title
 * holds every word first. Words match the start of a word in the title, product type, tags or
 * colors (or an alias's word). Only when nothing matches exactly, words over 4 letters may be off by
 * one letter ("fishng").
 * @param {IndexEntry[]} index
 * @param {string} query
 * @param {Map<string, string[]>} aliases
 */
function matchIndex(index, query, aliases) {
  const words = queryWords(query);
  if (!words.length) return [];
  const options = words.map((word) => [word, ...(aliases.get(word) ?? [])]);

  /** @param {string[]} tokens @param {string[]} alternatives @param {boolean} fuzzy */
  const has = (tokens, alternatives, fuzzy) =>
    tokens.some((token) =>
      alternatives.some(
        (word) =>
          token.startsWith(word) ||
          (fuzzy &&
            word.length > 4 &&
            [word.length - 1, word.length, word.length + 1].some(
              (length) => token.length >= length && withinOneEdit(word, token.slice(0, length))
            ))
      )
    );

  for (const fuzzy of [false, true]) {
    /** @type {string[]} */
    const inTitle = [];
    /** @type {string[]} */
    const elsewhere = [];
    for (const entry of index) {
      if (!options.every((alternatives) => has(entry.all, alternatives, fuzzy))) continue;
      (options.every((alternatives) => has(entry.title, alternatives, fuzzy)) ? inTitle : elsewhere).push(entry.id);
    }
    if (inTitle.length || elsewhere.length) return [...inTitle, ...elsewhere];
    if (!words.some((word) => word.length > 4)) break;
  }
  return [];
}

/**
 * Collection products listing.
 * - Filter pills (phones: the "Filter" dropdown), "Clear filters" and the sort menu update the listing in place through the
 *   Section Rendering API: no page reload, no jump to the top, and the URL stays shareable.
 *   Back/forward restore the matching results.
 * - "Show more goods" appends the next page of product cards.
 * - The search field searches the whole collection through Shopify search (scoped by the hidden
 *   tag and product type inputs in its form) and shows the results in this grid as you type.
 *   Clearing it restores the collection. The address keeps ?q= (replaceState, so no history entry
 *   per search) so a search can be shared or reloaded. Enter (the phone keyboard's Search key) runs
 *   the search in place instead of leaving for /search, and closes the phone keyboard. With no
 *   matches the grid shows "No goods match" with a Clear search button, announced in the status.
 * - On collection pages the search is collection-owned: the collection's compact index
 *   (sections/hl-collection-index.liquid) loads on intent (focus, hover or touch on the field;
 *   never on page load unless the address has ?q=), is cached 10 minutes in sessionStorage, and is
 *   matched here (title, type, tags, colors; every word at a word start; plural "s", Mt = Mount,
 *   hyphens and Theme settings aliases; one-letter typos only when nothing matches). The count is
 *   exact; cards come from Shopify search by id (32 at a time, "Show more goods" for the rest).
 *   Shopify's own word search returns different results for the same query, so it's only the
 *   fallback (and the search page's search).
 *   On the search page itself the results update the same way as you type (clearing it shows the
 *   page's suggestions again).
 * Without JavaScript the pills and "Show more" are normal links and the sort form submits.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - Product card list (absent when the collection is empty).
 * @property {HTMLElement} [more] - Wrapper around the "Show more goods" link.
 * @property {HTMLElement} status - Live region for announcements.
 *
 * @extends {Component<Refs>}
 */
class CollectionProductsComponent extends Component {
  requiredRefs = ['status'];

  /** @type {AbortController | null} */
  #request = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {number | null} */
  #searchTimer = null;

  /** @type {HTMLElement[] | null} Collection results saved while a search is showing. */
  #collectionResults = null;

  /** @type {string} Count text saved while a search is showing. */
  #collectionCount = '';

  /** @type {string | null} Section id of the listing on the search page, learned from the first response. */
  #searchSectionId = null;

  /** @type {{ key: string, promise: Promise<IndexEntry[]> } | null} Collection-owned search index. */
  #index = null;

  /** Increases with every search, so a slower earlier search never replaces a newer one. */
  #searchToken = 0;

  /** @type {AbortController | null} Card request for the collection-owned search. */
  #cardRequest = null;

  /** @type {{ ids: string[], shown: number } | null} The collection-owned search being shown. */
  #owned = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });

    // Collection-owned search: fetch the index when the visitor shows intent, never on page load.
    if (this.#isOwnedSearch) {
      const prefetch = (/** @type {Event} */ event) => {
        if (event.target instanceof Element && event.target.closest('.hl-collection-search')) this.#loadIndex().catch(() => {});
      };
      for (const type of ['focusin', 'pointerover', 'touchstart']) {
        this.addEventListener(type, prefetch, { signal, passive: true });
      }
    }

    // Restore a shared ?q= search on load.
    const input = this.#searchInput;
    const query = new URL(window.location.href).searchParams.get('q');
    if (input && query && !this.#isSearchPage) {
      input.value = query;
      this.#search(query, { updateUrl: false });
    }
  }

  /** Collection pages search their own products (sections/hl-collection-index.liquid). */
  get #isOwnedSearch() {
    return this.hasAttribute('data-hl-owned-search') && !this.#isSearchPage;
  }

  /**
   * Runs a search the right way for this page: the collection's own index, or Shopify search on
   * the search page.
   * @param {string} query
   * @param {{ updateUrl?: boolean }} [options]
   */
  #search(query, options) {
    return this.#isOwnedSearch ? this.#ownedSearch(query, options) : this.#runSearch(query, options);
  }

  /** The index URL: this collection with the page's sort and filters (so it lists what the page lists). */
  #indexUrl() {
    const url = new URL(this.dataset.collectionUrl ?? window.location.pathname, window.location.origin);
    for (const [name, value] of new URL(window.location.href).searchParams) {
      if (name === 'sort_by' || name.startsWith('filter.')) url.searchParams.append(name, value);
    }
    return url;
  }

  /**
   * Loads the collection's index once per page (sort and filters), from sessionStorage when it's
   * under 10 minutes old, otherwise from sections/hl-collection-index.liquid (250 per request,
   * later pages in parallel).
   * @returns {Promise<IndexEntry[]>}
   */
  #loadIndex() {
    const url = this.#indexUrl();
    const key = INDEX_STORAGE_PREFIX + url.pathname + url.search;
    if (this.#index?.key === key) return this.#index.promise;

    const promise = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) ?? 'null');
        if (cached && Date.now() - cached.time < INDEX_TTL && Array.isArray(cached.rows)) return buildIndex(cached.rows);
      } catch {
        // No storage (private mode, blocked): fetch it.
      }

      /** @param {number} page */
      const fetchPage = async (page) => {
        const pageUrl = new URL(url);
        pageUrl.searchParams.set('section_id', 'hl-collection-index');
        pageUrl.searchParams.set('page', String(page));
        const response = await fetch(pageUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = html.querySelector('script[data-hl-collection-index]')?.textContent;
        if (!data) throw new Error('Index markup missing');
        return /** @type {{ pages: number, products: IndexRow[] }} */ (JSON.parse(data));
      };

      const first = await fetchPage(1);
      const rest = await Promise.all(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => fetchPage(i + 2)));
      const rows = [first, ...rest].flatMap((page) => page.products);
      try {
        sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), rows }));
      } catch {
        // Storage full or blocked: the index still works for this page.
      }
      return buildIndex(rows);
    })();

    promise.catch(() => {
      if (this.#index?.promise === promise) this.#index = null;
    });
    this.#index = { key, promise };
    return promise;
  }

  /**
   * Product cards for these ids, in this order, from the search template's listing (the same
   * cards as collections: badges, stars, quick view, Add to Pack). Shopify search finds products
   * by id exactly ("id:1 OR id:2"), unlike word searches.
   * @param {string[]} ids
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchCards(ids) {
    this.#cardRequest?.abort();
    const request = new AbortController();
    this.#cardRequest = request;

    const url = new URL(this.dataset.searchUrl ?? '/search', window.location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    // The first request fetches the whole search page and learns its listing's section id
    // (template--…__main); later ones fetch only that section.
    const html = await this.#fetchSection(url.toString(), this.#searchSectionId, request);
    this.#searchSectionId ??= html.querySelector('hl-collection-products')?.getAttribute('data-section-id') ?? null;

    /** @type {Map<string, HTMLElement>} */
    const cards = new Map();
    for (const item of html.querySelectorAll('hl-collection-products .hl-collection-products__item')) {
      if (item instanceof HTMLElement && item.dataset.productId) cards.set(item.dataset.productId, item);
    }
    return ids.flatMap((id) => cards.get(id) ?? []);
  }

  /**
   * Collection-owned search: matches the index, shows the exact count, then the first cards.
   * Falls back to Shopify search if the index can't load.
   * @param {string} query
   * @param {{ updateUrl?: boolean }} [options]
   */
  async #ownedSearch(query, { updateUrl = true } = {}) {
    const token = ++this.#searchToken;
    const results = this.querySelector(':scope [ref="results"]');
    const count = this.querySelector(':scope [ref="count"]');
    if (!(results instanceof HTMLElement) || !count) return;

    if (!this.#collectionResults) {
      this.#collectionResults = [...results.children].map((child) => /** @type {HTMLElement} */ (child));
      this.#collectionCount = count.textContent ?? '';
    }

    this.setAttribute('aria-busy', 'true');
    try {
      const index = await this.#loadIndex();
      if (token !== this.#searchToken) return;

      const ids = matchIndex(index, query, parseAliases(this.dataset.aliases ?? ''));
      const countText = (this.dataset.countTemplate ?? '[count]').replace('[count]', String(ids.length));
      count.textContent = countText;

      if (updateUrl) {
        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set('q', query);
        pageUrl.searchParams.delete('page');
        history.replaceState(history.state, '', pageUrl);
      }

      if (!ids.length) {
        this.#owned = null;
        results.replaceChildren(this.#emptyNote(query));
        this.#announce((this.dataset.emptyTemplate ?? '').replace('[terms]', query));
        return;
      }

      const first = ids.slice(0, CARD_BATCH);
      const cards = await this.#fetchCards(first);
      if (token !== this.#searchToken) return;

      const grid = document.createElement('ul');
      grid.className = 'hl-collection-products__grid list-unstyled';
      grid.setAttribute('role', 'list');
      grid.setAttribute('ref', 'grid');
      grid.append(...cards);
      this.#owned = { ids, shown: first.length };
      this.#dropMissing(first, cards);
      results.replaceChildren(grid, ...this.#ownedMore());
      this.#announce(count.textContent?.trim() ?? countText);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (token !== this.#searchToken) return;
      // The index or cards didn't load: use Shopify search for this one.
      console.error(error);
      this.#owned = null;
      await this.#runSearch(query, { updateUrl });
    } finally {
      if (token === this.#searchToken) this.removeAttribute('aria-busy');
    }
  }

  /** "Show more goods (N remaining)" for the collection-owned search, or nothing when all are shown. */
  #ownedMore() {
    if (!this.#owned || this.#owned.shown >= this.#owned.ids.length) return [];
    const remaining = this.#owned.ids.length - this.#owned.shown;

    const wrapper = document.createElement('div');
    wrapper.className = 'hl-collection-products__more';
    wrapper.setAttribute('ref', 'more');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button hl-load-more';
    button.dataset.hlOwnedMore = '';
    const label = document.createElement('span');
    label.className = 'hl-label';
    label.textContent = this.dataset.loadMoreLabel ?? '';
    const countLabel = document.createElement('span');
    countLabel.className = 'hl-load-more__count';
    countLabel.textContent = (this.dataset.remainingTemplate ?? '([count])').replace('[count]', String(remaining));
    button.append(label, ' ', countLabel);
    // Reuse the collection's own arrow icon when the page has one.
    const arrow = this.#collectionResults
      ?.map((child) => child.querySelector('.hl-load-more .hl-link__arrow'))
      .find(Boolean);
    if (arrow) button.append(arrow.cloneNode(true));
    wrapper.append(button);
    return [wrapper];
  }

  /**
   * Drops ids whose card didn't come back (a product hidden from search can't be fetched by id),
   * so the count and "remaining" never promise a card that can't show.
   * @param {string[]} requested
   * @param {HTMLElement[]} cards
   */
  #dropMissing(requested, cards) {
    if (!this.#owned || cards.length === requested.length) return;
    const found = new Set(cards.map((card) => card.dataset.productId));
    const missing = new Set(requested.filter((id) => !found.has(id)));
    this.#owned.ids = this.#owned.ids.filter((id) => !missing.has(id));
    this.#owned.shown -= missing.size;
    const count = this.querySelector(':scope [ref="count"]');
    if (count) count.textContent = (this.dataset.countTemplate ?? '[count]').replace('[count]', String(this.#owned.ids.length));
  }

  /** @param {HTMLButtonElement} button */
  async #showMoreOwned(button) {
    const owned = this.#owned;
    if (!owned || button.getAttribute('aria-busy') === 'true') return;
    const token = this.#searchToken;
    const next = owned.ids.slice(owned.shown, owned.shown + CARD_BATCH);
    button.setAttribute('aria-busy', 'true');
    try {
      const cards = await this.#fetchCards(next);
      if (token !== this.#searchToken || this.#owned !== owned) return;
      this.querySelector(':scope [ref="results"] .hl-collection-products__grid')?.append(...cards);
      owned.shown += next.length;
      this.#dropMissing(next, cards);
      button.closest('.hl-collection-products__more')?.replaceWith(...this.#ownedMore());
      this.#announce(this.dataset.loadedTemplate?.replace('[count]', String(cards.length)) ?? '');
      const focusTarget = cards[0]?.querySelector('a[href]');
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * "No goods match “…”" with a Clear search button (the same markup the section renders).
   * @param {string} query
   */
  #emptyNote(query) {
    const note = document.createElement('div');
    note.className = 'hl-collection-products__empty hl-collection-products__empty--search';
    note.dataset.hlSearchEmptyNote = '';
    const text = document.createElement('p');
    text.className = 'hl-collection-products__empty-text';
    text.textContent = (this.dataset.emptyTemplate ?? '').replace('[terms]', query);
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'hl-link hl-collection-products__clear-search';
    clear.dataset.hlClearSearch = '';
    clear.textContent = this.dataset.clearLabel ?? '';
    note.append(text, clear);
    return note;
  }

  get #searchInput() {
    const input = this.querySelector('.hl-collection-search input[name="q"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #isSearchPage() {
    return window.location.pathname === (this.dataset.searchUrl ?? '/search');
  }

  /**
   * Search parameters for the form: its search URL (the form's own action is the collection, for
   * submits without JavaScript) and every hidden input, including the disabled scope inputs.
   * @param {HTMLFormElement} form
   */
  #searchUrl(form) {
    const url = new URL(form.dataset.searchAction || form.action, window.location.origin);
    for (const input of form.querySelectorAll('input[type="hidden"]')) {
      if (input instanceof HTMLInputElement && input.name) url.searchParams.append(input.name, input.value);
    }
    return url;
  }

  /** @param {Event} event */
  searchInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (this.#searchTimer !== null) clearTimeout(this.#searchTimer);
    this.#searchTimer = window.setTimeout(() => {
      const query = input.value.trim();
      if (query.length >= 2) {
        this.#search(query);
      } else if (query.length === 0) {
        // The search page shows its suggestions again (featured goods); collections restore their listing.
        if (this.#isSearchPage) {
          this.#runSearch('');
        } else {
          this.#clearSearch();
        }
      }
    }, 300);
  }

  /**
   * Shows Shopify search results for the query in this grid, keeping the search field in place.
   * @param {string} query
   * @param {{ updateUrl?: boolean }} [options]
   */
  async #runSearch(query, { updateUrl = true } = {}) {
    const form = this.querySelector('.hl-collection-search');
    if (!(form instanceof HTMLFormElement)) return;

    const url = this.#searchUrl(form);
    url.searchParams.set('q', query);
    // On the search page the results come from this same section.
    if (this.#isSearchPage) this.#searchSectionId ??= this.dataset.sectionId ?? null;

    const results = this.querySelector(':scope [ref="results"]');
    const count = this.querySelector(':scope [ref="count"]');
    if (!results || !count) return;

    if (!this.#collectionResults && !this.#isSearchPage) {
      this.#collectionResults = [...results.children].map((child) => /** @type {HTMLElement} */ (child));
      this.#collectionCount = count.textContent ?? '';
    }

    this.setAttribute('aria-busy', 'true');
    try {
      const html = await this.#fetchSection(url.toString(), this.#searchSectionId);
      const listing = html.querySelector('hl-collection-products');
      const nextResults = listing?.querySelector('[ref="results"]');
      const nextCount = listing?.querySelector('[ref="count"]');
      if (!listing || !nextResults || !nextCount) throw new Error('Search markup missing');

      // Learn the search page's section id so later requests can fetch just that section.
      this.#searchSectionId ??= listing.dataset.sectionId ?? null;

      results.replaceChildren(...nextResults.children);
      count.textContent = nextCount.textContent;
      // No matches: announce the message ("No goods match …"), not just "0 goods".
      const emptyNote = results.querySelector('[data-hl-search-empty-note] p');
      this.#announce((emptyNote?.textContent ?? nextCount.textContent ?? '').trim());

      if (updateUrl) {
        const pageUrl = new URL(window.location.href);
        if (query) {
          pageUrl.searchParams.set('q', query);
        } else {
          pageUrl.searchParams.delete('q');
        }
        pageUrl.searchParams.delete('page');
        history.replaceState(history.state, '', pageUrl);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // Stay on the collection; the listing keeps what it showed.
      console.error(error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #clearSearch() {
    this.#request?.abort();
    this.#cardRequest?.abort();
    this.#searchToken++;
    this.#owned = null;
    this.removeAttribute('aria-busy');
    const results = this.querySelector(':scope [ref="results"]');
    const count = this.querySelector(':scope [ref="count"]');
    if (this.#collectionResults && results && count) {
      results.replaceChildren(...this.#collectionResults);
      count.textContent = this.#collectionCount;
      this.#announce(this.#collectionCount.trim());
    }
    this.#collectionResults = null;

    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.delete('q');
    history.replaceState(history.state, '', pageUrl);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /** @param {Event} event */
  submitSort(event) {
    const select = event.target;
    if (select instanceof HTMLSelectElement) select.form?.requestSubmit();
  }

  /**
   * Phone "Filter" dropdown: each option's value is the same filtered URL as its pill.
   * @param {Event} event
   */
  selectFormat(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.value) return;
    this.#update(select.value, { focusSelector: '.hl-format-select select' });
  }

  /** @param {MouseEvent} event */
  async loadMore(event) {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    const nextUrl = link?.dataset.nextUrl;
    if (!link || !nextUrl) return;
    event.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;

    link.setAttribute('aria-busy', 'true');

    try {
      const html = await this.#fetchSection(nextUrl, link.dataset.sectionId ?? null);
      const items = html.querySelectorAll('.hl-collection-products__grid > li');
      const firstNew = items[0];

      const grid = link.closest('[ref="results"]')?.querySelector('.hl-collection-products__grid') ?? this.refs.grid;
      if (!grid) throw new Error('Grid missing');
      grid.append(...items);

      const currentMore = link.closest('.hl-collection-products__more');
      const nextMore = html.querySelector('.hl-collection-products__more');
      if (nextMore && currentMore) {
        currentMore.replaceWith(nextMore);
      } else {
        currentMore?.remove();
      }

      // Keep the address shareable (collection pages only) and move focus to the first new product.
      if (!this.#collectionResults) history.replaceState(history.state, '', new URL(nextUrl, window.location.origin));
      this.#announce(this.dataset.loadedTemplate?.replace('[count]', String(items.length)) ?? '');
      const focusTarget = firstNew instanceof HTMLElement ? firstNew.querySelector('a[href]') : null;
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      window.location.href = nextUrl;
    } finally {
      link.removeAttribute('aria-busy');
    }
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    // "Clear search" in the no-matches message: empty the field and restore the listing.
    const clear = event.target instanceof Element ? event.target.closest('[data-hl-clear-search]') : null;
    if (clear && this.contains(clear)) {
      const input = this.#searchInput;
      if (input) input.value = '';
      if (this.#searchTimer !== null) clearTimeout(this.#searchTimer);
      if (this.#isSearchPage) {
        this.#runSearch('');
      } else {
        this.#clearSearch();
      }
      input?.focus();
      return;
    }

    const more = event.target instanceof Element ? event.target.closest('[data-hl-owned-more]') : null;
    if (more instanceof HTMLButtonElement && this.contains(more)) {
      this.#showMoreOwned(more);
      return;
    }

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a.hl-filter-pill, [data-hl-filter-link]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    event.preventDefault();
    this.#update(link.href, { focusHref: link.getAttribute('href') });
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Search: Enter (or the phone keyboard's Search key) runs the search in place.
    if (form.classList.contains('hl-collection-search')) {
      event.preventDefault();
      const input = this.#searchInput;
      if (!input) return;
      if (this.#searchTimer !== null) clearTimeout(this.#searchTimer);
      const query = input.value.trim();
      if (query) {
        this.#search(query);
      } else if (this.#isSearchPage) {
        this.#runSearch('');
      } else {
        this.#clearSearch();
      }
      // Phones: close the keyboard so the results are visible.
      if (window.matchMedia('(max-width: 749px), (pointer: coarse)').matches) input.blur();
      return;
    }

    if (!form.classList.contains('hl-sort')) return;
    event.preventDefault();

    const url = new URL(form.action, window.location.origin);
    for (const [name, value] of new FormData(form)) {
      if (typeof value === 'string') url.searchParams.append(name, value);
    }
    this.#update(url.toString(), { focusSelector: '.hl-sort select' });
  };

  #handlePopState = () => {
    if (window.location.pathname !== new URL(this.dataset.collectionUrl ?? '', window.location.origin).pathname) return;
    this.#update(window.location.href, { push: false });
  };

  /**
   * Fetches this section for a collection URL and swaps in the new listing.
   * @param {string} url - Collection URL including filter/sort parameters.
   * @param {{ push?: boolean, focusHref?: string | null, focusSelector?: string | null }} [options]
   *   focusSelector: the control to refocus after the swap (the sort menu or the Filter dropdown).
   */
  async #update(url, { push = true, focusHref = null, focusSelector = null } = {}) {
    const target = new URL(url, window.location.origin);
    this.#owned = null;
    target.searchParams.delete('page');
    target.searchParams.delete('q');
    this.#collectionResults = null;

    this.setAttribute('aria-busy', 'true');
    try {
      const html = await this.#fetchSection(target.toString());
      const next = html.querySelector('hl-collection-products');
      if (!next) throw new Error('Listing markup missing');

      // Swap everything except the live region, so announcements still reach screen readers.
      const status = this.querySelector(':scope > [ref="status"]');
      for (const child of [...this.children]) if (child !== status) child.remove();
      const incoming = [...next.children].filter((child) => !child.matches('[ref="status"]'));
      if (status) {
        status.before(...incoming);
      } else {
        this.append(...incoming);
      }
      if (push) history.pushState({ hlCollection: true }, '', target);

      const count = this.querySelector('.hl-collection-products__count');
      this.#announce(count?.textContent?.trim() ?? '');

      // Keep keyboard focus where it was: on the chosen pill, or on the dropdown that changed.
      const focusTarget = focusSelector
        ? this.querySelector(focusSelector)
        : focusHref
          ? [...this.querySelectorAll('a.hl-filter-pill')].find((pill) => pill.getAttribute('href') === focusHref) ??
            this.querySelector('a.hl-filter-pill[aria-current]')
          : null;
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      window.location.href = target.toString();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} url
   * @param {string | null} [sectionId] - Section to render; defaults to this listing. Pass an empty
   *   string to fetch the full page (used for the first search, before the search section id is known).
   * @param {AbortController | null} [controller] - Own request controller (collection-owned search cards),
   *   so card requests don't cancel listing updates.
   * @returns {Promise<Document>}
   */
  async #fetchSection(url, sectionId = this.dataset.sectionId ?? '', controller = null) {
    let signal;
    if (controller) {
      signal = controller.signal;
    } else {
      this.#request?.abort();
      this.#request = new AbortController();
      signal = this.#request.signal;
    }

    const sectionUrl = new URL(url, window.location.origin);
    const isSearchUrl = sectionUrl.pathname === (this.dataset.searchUrl ?? '/search');
    const id = sectionId ?? (isSearchUrl ? '' : this.dataset.sectionId ?? '');
    if (id) sectionUrl.searchParams.set('section_id', id);
    const response = await fetch(sectionUrl, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    // DOMParser parses with scripting off, so <noscript> fallbacks (the sort button, the phone
    // pill styles) would come in as live markup. They're only for pages without JavaScript.
    for (const fallback of html.querySelectorAll('noscript')) fallback.remove();
    return html;
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector(':scope > [ref="status"]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('hl-collection-products')) {
  customElements.define('hl-collection-products', CollectionProductsComponent);
}
